import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BodyRequest, FunctionsAvailable, ItemType } from '../formulario/request.model';
import { Field } from '../formulario/field.model';
import { FORM_BUTTONS } from '../formulario/form-buttons.config';
import { FormButtonType } from '../formulario/formButtonType.enum';
import { ListadoComponent } from '../listado/listado.component';
import { FormularioComponent } from '../formulario/formulario.component';
import { AsociacionComponent } from '../asociaciones/asociacion/asociacion.component';
import { EjercicioItemComponent } from '../ejercicios/ejercicio/ejercicio.component';
import { CargoComponent } from '../cargos/cargo/cargo.component';
import { AsociadoComponent } from '../asociados/asociado/asociado.component';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormularioService } from '../../services/formulario.service';
import { PermisosService } from '../../services/permisos.service';
import { MigasService } from '../../services/migas.service';
import { PageHeaderComponent, SeccionDiseno } from '../ui/page-header.component';

export type ModeType = 'list' | 'Crear' | 'Editar' | 'showItem';

export type FunctionsMap = {
  [key: string]: FunctionsAvailable;
};

@Component({
  selector: 'app-core',
  standalone: true,
  imports: [ListadoComponent, FormularioComponent, AsociacionComponent, EjercicioItemComponent, CargoComponent, AsociadoComponent, CommonModule, PageHeaderComponent],
  templateUrl: './core.component.html',
  styleUrl: './core.component.scss'
})
export class CoreComponent {
  ItemType = ItemType;

  loading = true;

  @Input()
  type!: ItemType;
  @Input()
  titleForm = 'Crear ejercicio';
  @Input()
  items!: BodyRequest[];
  
  @Input()
  itemSelected!: BodyRequest;

  /** El listado lo aporta el contenido proyectado (p. ej. listado paginado de asociados) en lugar de app-listado con `items`. */
  @Input()
  listadoPersonalizado = false;

  itemParsed: any;

  parent!: BodyRequest;

  mode: ModeType = 'list';
  buttons = [
    FORM_BUTTONS[FormButtonType.Borrar],
    FORM_BUTTONS[FormButtonType.Cancelar],
  ]
  
  functionSelected!: FunctionsAvailable;
  public fields: Field[] = [];
  title: string = '';

  bgClass: string = 'bg-';

  constructor(
    private router: Router,
    private formularioService: FormularioService,
    protected permisos: PermisosService,
    private migas: MigasService
  ) {}

  // Permiso de escritura global para crear cada tipo de elemento (UX: la API vuelve a comprobarlo).
  private static readonly PERMISO_CREAR: Partial<Record<ItemType, string>> = {
    [ItemType.ASOCIACIONES]: 'asociaciones:escribir',
    [ItemType.ASOCIADOS]: 'asociados:escribir',
    [ItemType.CARGOS]: 'cargos:gestionar',
    [ItemType.EJERCICIOS]: 'ejercicios:gestionar',
    [ItemType.TIPOASOCIACIONES]: 'tipos:gestionar',
    [ItemType.HISTORICO]: 'historico:escribir'
  };

  // Presentación de cada listado (Wave 7): título, icono, sección de color y texto del botón de alta.
  private static readonly PRESENTACION: Partial<Record<ItemType, { titulo: string; descripcion: string; icono: string; seccion: SeccionDiseno; crear: string }>> = {
    [ItemType.ASOCIACIONES]: { titulo: 'Asociaciones', descripcion: 'Fogueras y barracas de la Federación.', icono: 'bi-people-fill', seccion: 'asociaciones', crear: 'Nueva asociación' },
    [ItemType.ASOCIADOS]: { titulo: 'Asociados', descripcion: 'Busca personas y abre su ficha.', icono: 'bi-person-fill', seccion: 'asociados', crear: 'Nuevo asociado' },
    [ItemType.EJERCICIOS]: { titulo: 'Ejercicios', descripcion: 'Ejercicio vigente y anteriores.', icono: 'bi-calendar-fill', seccion: 'ejercicios', crear: 'Nuevo ejercicio' },
    [ItemType.CARGOS]: { titulo: 'Cargos', descripcion: 'Catálogo de cargos de las asociaciones.', icono: 'bi-award-fill', seccion: 'cargos', crear: 'Nuevo cargo' }
  };

  get presentacion() {
    return CoreComponent.PRESENTACION[this.type] ?? { titulo: String(this.type ?? ''), descripcion: '', icono: 'bi-grid-fill', seccion: '' as const, crear: 'Crear' };
  }

  puedeCrear(): boolean {
    const permiso = CoreComponent.PERMISO_CREAR[this.type];
    return Boolean(permiso) && this.permisos.tieneGlobal(permiso!);
  }

  ngOnChanges() {
    this.loading = true;
    this.loadStyleClass();
    if (this.mode === 'list') {
      this.title = `Listado de ${this.type}`
    }    
  }

  loadStyleClass() {
    this.bgClass = `py-3 px-md-5 px-3 mb-2 bg-listado-title bg-${this.type.toLowerCase()}`;
  }


  public loadFormConfig(functionType: string) {
    let {fields, extraFields, buttons, titleForm, mode, type, functionSelected} = this.formularioService.loadFormConfig(functionType, this.buttons, this.itemSelected, this.mode, this.type, this.titleForm);
    this.fields = [...fields, ...extraFields];
    this.buttons = buttons;
    this.titleForm = titleForm;
    this.functionSelected = FunctionsAvailable[functionSelected as keyof typeof FunctionsAvailable];
    this.mode = mode;
    this.type = type;
    this.loading = false;
    this.migas.fijarExtra(mode === 'list' ? null : this.titleForm);
  }

  openItem(event: any){
    if (this.type === ItemType.ASOCIADOS) {
      this.router.navigateByUrl(`${this.type.toLowerCase()}/${event.nif}`);
    } else if (this.type === ItemType.EJERCICIOS) {
      this.router.navigateByUrl(`${this.type.toLowerCase()}/${event.ejercicio}`)  
    } else {
      this.router.navigateByUrl(`${this.type.toLowerCase()}/${event.id}`)
    }
  }

  openItemFromItem(event: any){
    if (this.formularioService.isItemEvent(event) && !this.formularioService.isFormEvent(event)) {
      this.parent = event.parent;
      this.itemSelected = event.item;
      this.type = event.type
      this.mode = 'showItem';
    } else if(this.formularioService.isFormEvent(event)) {
      this.itemSelected = event.item;
      this.loadFormConfig(event.functionSelected);
    } else {
      this.itemSelected = event;
      this.mode = 'showItem';
    }
  }

  back(event?: any) {
    // Al salir de «Crear/Editar» desaparece el último tramo de la ruta (después se fija de nuevo si el modo cambia)
    setTimeout(() => this.migas.fijarExtra(this.mode === 'list' || this.mode === 'showItem' ? null : this.titleForm));
    if (Boolean(event) && event.mode === 'showItem') {
      this.openItemFromItem({type: event.type, item: event.item});
    } else {
      switch (this.mode) {
        case 'showItem':
          this.mode = 'list'
          break;
        case 'Crear':
          if (this.type === ItemType.POSTREQUESTASOCIADO) {
            this.type = ItemType.ASOCIADOS;
          }
          this.mode = 'list'
          break;
        case 'Editar':
          this.mode = 'list'
          break;
      
        default:
          break;
      }
    }
  }

}
